import { CODE_ESCAPE } from "common/constants";
import type { ReactNode } from "react";
import { useRef } from "react";
import Button from "ui/buttons/Button";
import type { ModalProps } from "ui/components/Modal";
import Modal from "ui/components/Modal";

export interface SteppedModalProps extends Omit<ModalProps, "open"> {
  children: ReactNode;
  headerText: string;
  steps: string[];
  stepIndex: number;
  onClickBack: () => void;
  onClickNext: () => void;
  canProceed?: boolean;
}

export default function SteppedModal({
  headerText,
  onClickBack,
  onClickNext,
  stepIndex,
  steps,
  canProceed,
  children,
  size = "small",
  ...props
}: SteppedModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.code === CODE_ESCAPE && props.onClose) {
      props.onClose();
    }
  };

  return (
    <Modal
      open
      title={headerText}
      size={size}
      variants={["bodyPadding", "footerButtons"]}
      footer={
        <>
          <Button type="button" btnSize="large" btnStyle="tertiary" onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="button" btnSize="large" btnStyle="tertiary" onClick={onClickBack} disabled={stepIndex === 0}>
            Back
          </Button>
          {stepIndex !== steps.length - 1 && (
            <Button type="button" btnSize="large" btnStyle="primary" disabled={!canProceed} onClick={onClickNext}>
              Next
            </Button>
          )}
          {stepIndex === steps.length - 1 && (
            <Button type="submit" btnSize="large" btnStyle="primary" form="stepped-modal">
              Submit
            </Button>
          )}
        </>
      }
      {...props}
    >
      <div
        ref={(node) => {
          modalRef.current = node;
          // Focus immediately when the element is attached to the DOM
          if (node) {
            node.focus();
          }
        }}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <form
          id="stepped-modal"
          onSubmit={(e) => {
            e.preventDefault();
            onClickNext();
          }}
        >
          {children}
        </form>
      </div>
    </Modal>
  );
}
